import React, { useState, useMemo } from 'react';

/* ==========================================
   HELPER: TITLE CASE FORMATTER
   ========================================== */
const toTitleCase = (str) => {
  if (!str || typeof str !== 'string') return str || '';
  return str
    .trim()
    .toLowerCase()
    .split(' ')
    .map(word => {
      if (!word) return '';
      if (word.includes('-')) {
        return word.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('-');
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

/* ==========================================
   HELPER: DYNAMIC TERM TRANSLATOR (EN -> PL)
   ========================================== */
const translateTerm = (term, lang = 'pl') => {
  if (!term || typeof term !== 'string') return term || '-';
  if (lang !== 'pl') return toTitleCase(term);

  const text = term.trim();
  const lower = text.toLowerCase();

  if (text.includes(',')) {
    return text.split(',').map(s => translateTerm(s, lang)).join(', ');
  }

  const dictionary = {
    'upper legs': 'Uda i Pośladki',
    'upper_legs': 'Uda i Pośladki',
    'lower legs': 'Łydki',
    'lower_legs': 'Łydki',
    'upper arms': 'Ramiona',
    'upper_arms': 'Ramiona',
    'lower arms': 'Przedramiona',
    'lower_arms': 'Przedramiona',
    'chest': 'Klatka piersiowa',
    'back': 'Plecy i Grzbiet',
    'shoulders': 'Barki',
    'waist': 'Brzuch / Pas',
    'waist / core': 'Brzuch / Pas',
    'cardio': 'Kardio',
    'neck': 'Szyja',
    'legs': 'Nogi',
    'body weight': 'Ciężar ciała',
    'bodyweight': 'Ciężar ciała',
    'dumbbell': 'Hantle',
    'dumbbells': 'Hantle',
    'barbell': 'Sztanga',
    'cable': 'Wyciąg',
    'leverage machine': 'Maszyna',
    'machine': 'Maszyna',
    'band': 'Guma oporowa',
    'smith machine': 'Suwnica Smitha',
    'kettlebell': 'Kettlebell',
    'kettlebells': 'Kettlebell',
    'weighted': 'Z dociążeniem',
    'stability ball': 'Piłka gimnastyczna',
    'ez barbell': 'Sztanga łamana',
    'ez_barbell': 'Sztanga łamana',
    'other': 'Inny',
    'quads': 'Czworogłowy uda',
    'quadriceps': 'Czworogłowy uda',
    'hamstrings': 'Kulszowo-goleniowe',
    'glutes': 'Pośladki',
    'gluteus maximus': 'Pośladkowy większy',
    'pectoralis major': 'Piersiowy większy',
    'latissimus dorsi': 'Najszerszy grzbietu',
    'deltoids': 'Naramienne (barki)',
    'deltoid': 'Naramienny',
    'biceps': 'Biceps (dwugłowy ramienia)',
    'triceps': 'Triceps (trójgłowy ramienia)',
    'abs': 'Prosty brzucha',
    'core': 'Mięśnie głębokie (core)',
    'calves': 'Brzuchaty łydki',
    'trapezius': 'Czworoboczny (kaptury)',
    'rhomboids': 'Równoległoboczne',
    'erector spinae': 'Prostownik grzbietu',
    'lower back': 'Dolny grzbiet',
    'upper back': 'Górny grzbiet',
    'obliques': 'Skośne brzucha',
    'forearms': 'Przedramiona'
  };

  if (dictionary[lower]) return dictionary[lower];

  for (const [key, translated] of Object.entries(dictionary)) {
    if (lower.includes(key)) {
      return lower.replace(key, translated);
    }
  }

  return toTitleCase(text);
};

export function InteractiveWorkoutBuilder({
  initialTitle = "",
  initialTag = "#FBW",
  initialExercises = [],
  isGlobalOnly = false,
  allExercises = [],
  customExercises = [],
  onSave,
  onCancel,
  lang = 'pl',
  assignedClientId = 'ALL',
  setAssignedClientId = null,
  activeTrainees = []
}) {
  // Title & Tag State
  const [title, setTitle] = useState(initialTitle);
  const [tag, setTag] = useState(initialTag || "#FBW");
  const [customTagInput, setCustomTagInput] = useState("");
  const [clientTarget, setClientTarget] = useState(assignedClientId);
  const [isSaving, setIsSaving] = useState(false);

  // Active Exercises Timeline State
  const [exercises, setExercises] = useState(() => {
    return initialExercises.map((ex, idx) => {
      const defaultInstr = (
        lang === 'pl'
          ? (ex.instructionsPL || ex.hintsPL || (Array.isArray(ex.instructions) ? ex.instructions.join(' ') : ex.instructions) || ex.hints || ex.notes || "")
          : (ex.instructionsEN || ex.hintsEN || (Array.isArray(ex.instructions) ? ex.instructions.join(' ') : ex.instructions) || ex.hints || ex.notes || "")
      ).trim();

      const setsCount = parseInt(ex.setsCount || ex.sets || 3) || 3;
      const defaultRestTimer = ex.defaultRest || "90s";

      const defaultSetsList = Array.from({ length: setsCount }, (_, i) => ({
        setNum: i + 1,
        type: i === 0 && setsCount > 2 ? 'Warm-up' : 'Normal',
        reps: ex.reps || "10–12",
        weight: ex.weight || "",
        weightUnit: ex.weightUnit || "kg",
        rpe: "8",
        rest: defaultRestTimer
      }));

      return {
        ...ex,
        uid: ex.uid || `ex_item_${Date.now()}_${idx}_${Math.random().toString(36).substr(2, 4)}`,
        name: toTitleCase(ex.name || ""),
        namePL: toTitleCase(ex.namePL || ex.name || ""),
        nameEN: toTitleCase(ex.nameEN || ex.name || ""),
        bodyPart: ex.bodyPart || ex.category || "General",
        equipment: ex.equipment || "Other",
        setsCount: setsCount,
        sets: setsCount,
        reps: ex.reps || "10–12",
        defaultRest: defaultRestTimer,
        comment: ex.comment || ex.notes || ex.hints || defaultInstr,
        notes: ex.notes || ex.hints || ex.comment || defaultInstr,
        hints: ex.hints || ex.notes || ex.comment || defaultInstr,
        supersetGroup: ex.supersetGroup || null,
        notesCollapsed: false,
        setsList: Array.isArray(ex.setsList) && ex.setsList.length > 0 ? ex.setsList : defaultSetsList
      };
    });
  });

  // Catalog Filters State
  const [catalogTab, setCatalogTab] = useState('global');
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMuscle, setSelectedMuscle] = useState("ALL");
  const [selectedEquipment, setSelectedEquipment] = useState("ALL");
  const [mobileTab, setMobileTab] = useState("timeline"); // 'timeline' | 'catalog'
  const [previewMedia, setPreviewMedia] = useState(null);

  // Check if any filter is active in catalog
  const hasActiveFilters = useMemo(() => {
    return selectedMuscle !== 'ALL' || selectedEquipment !== 'ALL' || searchQuery.trim() !== '';
  }, [selectedMuscle, selectedEquipment, searchQuery]);

  const handleClearFilters = () => {
    setSelectedMuscle('ALL');
    setSelectedEquipment('ALL');
    setSearchQuery('');
  };

  // Compute Exercise Catalog List
  const availableCatalog = useMemo(() => {
    let list = [];
    if (isGlobalOnly || catalogTab === 'global') {
      list = allExercises.filter(ex => 
        ex.isGlobal === true || 
        ex.trainerId === 'global' || 
        ex.trainerId === 'all' || 
        (typeof ex.id === 'string' && ex.id.startsWith('ex_global_')) ||
        (!ex.trainerId && ex.isCustom !== true)
      );
    } else {
      list = customExercises.length > 0 ? customExercises : allExercises.filter(ex => ex.isCustom === true);
    }

    return list.filter(ex => {
      if (selectedMuscle !== "ALL") {
        const bodyStr = (ex.bodyPart || ex.category || ex.targetMuscle || "").toLowerCase();
        if (!bodyStr.includes(selectedMuscle.toLowerCase())) return false;
      }
      if (selectedEquipment !== "ALL") {
        const eqStr = (ex.equipment || "").toLowerCase();
        if (!eqStr.includes(selectedEquipment.toLowerCase())) return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const nameStr = ((ex.name || "") + " " + (ex.namePL || "") + " " + (ex.nameEN || "")).toLowerCase();
        if (!nameStr.includes(q)) return false;
      }
      return true;
    });
  }, [allExercises, customExercises, catalogTab, isGlobalOnly, selectedMuscle, selectedEquipment, searchQuery]);

  // Live Telemetry Calculations
  const telemetry = useMemo(() => {
    let totalExercises = exercises.length;
    let totalSets = 0;
    let totalRestSeconds = 0;

    const muscleCounts = {};

    exercises.forEach(ex => {
      const setsInEx = ex.setsList?.length || parseInt(ex.setsCount || ex.sets) || 3;
      totalSets += setsInEx;

      (ex.setsList || []).forEach(s => {
        const restSec = parseInt(s.rest) || 90;
        totalRestSeconds += restSec;
      });

      const muscle = translateTerm(ex.bodyPart || ex.category || "Inny", lang);
      muscleCounts[muscle] = (muscleCounts[muscle] || 0) + setsInEx;
    });

    const totalExecutionSeconds = totalSets * 45;
    const totalDurationMinutes = Math.max(10, Math.round((totalExecutionSeconds + totalRestSeconds) / 60));

    const musclePills = Object.entries(muscleCounts).map(([muscle, count]) => ({
      muscle,
      count,
      percentage: Math.round((count / (totalSets || 1)) * 100)
    })).sort((a, b) => b.count - a.count);

    return {
      totalExercises,
      totalSets,
      totalDurationMinutes,
      musclePills
    };
  }, [exercises, lang]);

  // Add Exercise to Timeline
  const handleAddExerciseToTimeline = (ex) => {
    const defaultInstr = (
      lang === 'pl'
        ? (ex.instructionsPL || ex.hintsPL || (Array.isArray(ex.instructions) ? ex.instructions.join(' ') : ex.instructions) || ex.hints || ex.notes || "")
        : (ex.instructionsEN || ex.hintsEN || (Array.isArray(ex.instructions) ? ex.instructions.join(' ') : ex.instructions) || ex.hints || ex.notes || "")
    ).trim();

    const defaultRest = "90s";

    const defaultSetsList = [
      { setNum: 1, type: 'Warm-up', reps: '12', weight: '', weightUnit: 'kg', rpe: '7', rest: '60s' },
      { setNum: 2, type: 'Normal', reps: '10', weight: '', weightUnit: 'kg', rpe: '8', rest: defaultRest },
      { setNum: 3, type: 'Normal', reps: '8-10', weight: '', weightUnit: 'kg', rpe: '8.5', rest: defaultRest }
    ];

    const newEx = {
      ...ex,
      uid: `ex_item_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
      name: toTitleCase(ex.name || ""),
      namePL: toTitleCase(ex.namePL || ex.name || ""),
      nameEN: toTitleCase(ex.nameEN || ex.name || ""),
      bodyPart: ex.bodyPart || ex.category || "General",
      equipment: ex.equipment || "Other",
      setsCount: 3,
      sets: 3,
      reps: "10",
      defaultRest: defaultRest,
      comment: defaultInstr,
      notes: defaultInstr,
      hints: defaultInstr,
      supersetGroup: null,
      notesCollapsed: false,
      setsList: defaultSetsList
    };

    setExercises(prev => [...prev, newEx]);
  };

  // Set Row Mutations (INHERIT PREVIOUS SET REPS, LOAD, RPE & REST)
  const handleAddSetRow = (exIdx) => {
    const updated = [...exercises];
    const currentSets = updated[exIdx].setsList || [];
    const lastSet = currentSets[currentSets.length - 1] || { 
      reps: '10', 
      weight: '', 
      weightUnit: 'kg', 
      rpe: '8', 
      rest: updated[exIdx].defaultRest || '90s' 
    };

    const newSetRow = {
      setNum: currentSets.length + 1,
      type: 'Normal',
      reps: lastSet.reps || '10',
      weight: lastSet.weight || '',
      weightUnit: lastSet.weightUnit || 'kg',
      rpe: lastSet.rpe || '8',
      rest: updated[exIdx].defaultRest || lastSet.rest || '90s'
    };

    const newSetsList = [...currentSets, newSetRow];
    updated[exIdx].setsList = newSetsList;
    updated[exIdx].setsCount = newSetsList.length;
    updated[exIdx].sets = newSetsList.length;

    setExercises(updated);
  };

  const handleRemoveSetRow = (exIdx, setIdx) => {
    const updated = [...exercises];
    const currentSets = updated[exIdx].setsList || [];
    if (currentSets.length <= 1) return;

    const filtered = currentSets.filter((_, i) => i !== setIdx).map((s, idx) => ({ ...s, setNum: idx + 1 }));
    updated[exIdx].setsList = filtered;
    updated[exIdx].setsCount = filtered.length;
    updated[exIdx].sets = filtered.length;

    setExercises(updated);
  };

  const handleUpdateSetField = (exIdx, setIdx, field, value) => {
    const updated = [...exercises];
    const newSetsList = [...updated[exIdx].setsList];
    newSetsList[setIdx] = { ...newSetsList[setIdx], [field]: value };
    updated[exIdx].setsList = newSetsList;
    setExercises(updated);
  };

  const handleUpdateDefaultRest = (exIdx, newRest) => {
    const updated = [...exercises];
    updated[exIdx].defaultRest = newRest;
    if (updated[exIdx].setsList) {
      updated[exIdx].setsList = updated[exIdx].setsList.map(s => ({ ...s, rest: newRest }));
    }
    setExercises(updated);
  };

  const handleToggleSuperset = (exIdx) => {
    const updated = [...exercises];
    const current = updated[exIdx].supersetGroup;
    const nextGroup = !current ? 'A' : (current === 'A' ? 'B' : null);
    updated[exIdx].supersetGroup = nextGroup;
    setExercises(updated);
  };

  // Submit Handler
  const handleFormSubmit = async () => {
    const finalTag = tag === 'CUSTOM' ? (customTagInput.trim() || '#FBW') : tag;
    if (!title.trim() || exercises.length === 0) {
      alert(lang === 'pl' ? "Wpisz nazwę szablonu i dodaj przynajmniej jedno ćwiczenie." : "Please enter a routine title and add at least one exercise.");
      return;
    }

    setIsSaving(true);
    try {
      await onSave({
        title: toTitleCase(title.trim()),
        tag: finalTag,
        assignedClientId: clientTarget,
        exercises: exercises.map(ex => ({
          ...ex,
          name: toTitleCase(ex.name),
          namePL: toTitleCase(ex.namePL || ex.name),
          nameEN: toTitleCase(ex.nameEN || ex.name),
          setsCount: ex.setsList?.length || ex.setsCount || 3,
          sets: ex.setsList?.length || ex.sets || 3,
          reps: ex.setsList?.[0]?.reps || ex.reps || "10",
          notes: ex.comment || ex.notes || ex.hints || "",
          hints: ex.comment || ex.hints || ex.notes || ""
        }))
      });
    } catch (err) {
      console.error("Save routine error:", err);
      alert("Error saving routine: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-[#0B0F19] text-slate-100 rounded-2xl border border-slate-800 shadow-2xl overflow-hidden flex flex-col h-[calc(100vh-90px)] min-h-[700px]">
      
      {/* 1. TOP HEADER TOOLBAR — CLEAN ALIGNED CONTROLS */}
      <header className="bg-slate-900/95 backdrop-blur-md border-b border-slate-800/90 p-4 lg:px-6 lg:py-4 space-y-3 shrink-0">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          
          {/* Inputs Row: Title + Tag Selector + Assign Selector */}
          <div className="flex-1 flex items-center gap-2.5 flex-wrap">
            {/* Routine Title Input */}
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={lang === 'pl' ? "Nazwa Szablonu (np. FBW A - Góra Ciała)..." : "Routine Title (e.g. Full Body A)..."}
              autoFocus
              className="bg-slate-950 border border-slate-800 focus:border-indigo-500 text-white font-bold text-sm lg:text-base rounded-xl px-4 h-11 outline-none flex-1 min-w-[260px] shadow-inner transition"
            />

            {/* Tag Selector */}
            <select
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              className="bg-slate-950 border border-slate-800 text-indigo-300 font-bold text-xs rounded-xl px-3 h-11 outline-none cursor-pointer shadow-inner shrink-0"
            >
              <option value="#FBW">#FBW (Full Body)</option>
              <option value="#Split">#Split (Kulturystyka)</option>
              <option value="#Góra / Dół">#Góra / Dół (Upper/Lower)</option>
              <option value="#Push / Pull / Legs">#Push / Pull / Legs</option>
              <option value="#Kondycja / Cardio">#Kondycja / Cardio</option>
              <option value="#Rehabilitacja">#Rehabilitacja</option>
              <option value="CUSTOM">{lang === 'pl' ? "+ Własny Tag..." : "+ Custom Tag..."}</option>
            </select>

            {tag === 'CUSTOM' && (
              <input
                type="text"
                value={customTagInput}
                onChange={(e) => setCustomTagInput(e.target.value)}
                placeholder="np. Hypertrophy"
                className="bg-slate-950 border border-slate-800 text-white font-bold text-xs rounded-xl px-3 h-11 outline-none focus:border-indigo-500 w-32 shadow-inner"
              />
            )}

            {/* Target Client Selector (Coach mode: Only ALL trainees or specific dedicated trainee) */}
            {!isGlobalOnly && activeTrainees && activeTrainees.length > 0 && (
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold hidden xl:inline">{lang === 'pl' ? 'Przypisz:' : 'Assign:'}</span>
                <select
                  value={clientTarget === 'GLOBAL' ? 'ALL' : clientTarget}
                  onChange={(e) => {
                    setClientTarget(e.target.value);
                    if (setAssignedClientId) setAssignedClientId(e.target.value);
                  }}
                  className="bg-slate-950 border border-slate-800 text-emerald-400 font-bold text-xs rounded-xl px-3 h-11 outline-none cursor-pointer shadow-inner"
                >
                  <option value="ALL">{lang === 'pl' ? 'Wszyscy Moi Zawodnicy' : 'All My Trainees'}</option>
                  {activeTrainees.map(t => (
                    <option key={t.id} value={t.id}>{t.name || t.displayName || t.email}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Action CTAs: Cancel & Primary Save */}
          <div className="flex items-center gap-2.5 shrink-0 justify-end">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="px-4 h-11 rounded-xl text-xs font-bold text-slate-400 hover:text-white transition cursor-pointer"
              >
                {lang === 'pl' ? 'Anuluj' : 'Cancel'}
              </button>
            )}
            
            {/* Primary Save Button */}
            <button
              type="button"
              disabled={isSaving || !title.trim() || exercises.length === 0}
              onClick={handleFormSubmit}
              className="bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white font-extrabold text-xs px-6 h-11 rounded-xl shadow-lg shadow-emerald-950/40 disabled:opacity-40 cursor-pointer transition flex items-center gap-2 border border-emerald-500/30"
            >
              <span className="material-symbols-outlined text-base">save</span>
              <span>{isSaving ? (lang === 'pl' ? 'Zapisywanie...' : 'Saving...') : (lang === 'pl' ? 'Zapisz Szablon Treningowy' : 'Save Routine Template')}</span>
            </button>
          </div>
        </div>

        {/* Telemetry Row & Muscle Volume Bar */}
        <div className="flex items-center justify-between gap-3 flex-wrap pt-2.5 border-t border-slate-800/80 text-xs font-semibold">
          <div className="flex items-center gap-2 flex-wrap text-slate-300">
            <div className="flex items-center gap-1.5 bg-slate-950 border border-slate-800 px-3 py-1.5 rounded-lg text-slate-300">
              <span className="material-symbols-outlined text-sm text-indigo-400">fitness_center</span>
              <span>{telemetry.totalExercises} {lang === 'pl' ? 'Ćwiczeń' : 'Movements'}</span>
            </div>

            <div className="flex items-center gap-1.5 bg-slate-950 border border-slate-800 px-3 py-1.5 rounded-lg text-slate-300">
              <span className="material-symbols-outlined text-sm text-cyan-400">bolt</span>
              <span>{telemetry.totalSets} {lang === 'pl' ? 'Serii Łącznie' : 'Total Sets'}</span>
            </div>

            <div className="flex items-center gap-1.5 bg-slate-950 border border-slate-800 px-3 py-1.5 rounded-lg text-slate-300">
              <span className="material-symbols-outlined text-sm text-slate-400">schedule</span>
              <span>~{telemetry.totalDurationMinutes} min</span>
            </div>
          </div>

          {/* Muscle Distribution Indicators */}
          <div className="flex items-center gap-1.5 flex-wrap ml-auto">
            {telemetry.musclePills.slice(0, 4).map((p, idx) => (
              <span key={idx} className="bg-slate-950 border border-slate-800 text-slate-300 text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-md">
                {p.muscle}: {p.percentage}%
              </span>
            ))}
          </div>
        </div>

        {/* Multi-Segment Muscle Volume Distribution Progress Bar */}
        {telemetry.musclePills.length > 0 && (
          <div className="w-full pt-1.5">
            <div className="flex h-2 w-full rounded-full overflow-hidden bg-slate-950 border border-slate-800 p-0.5 shadow-inner">
              {telemetry.musclePills.map((p, idx) => {
                const barColors = [
                  'bg-indigo-500', 'bg-cyan-500', 'bg-emerald-500', 'bg-amber-500', 
                  'bg-purple-500', 'bg-teal-500', 'bg-slate-400'
                ];
                return (
                  <div
                    key={idx}
                    style={{ width: `${p.percentage}%` }}
                    className={`h-full ${barColors[idx % barColors.length]} transition-all duration-300`}
                    title={`${p.muscle}: ${p.percentage}% (${p.count} ${lang === 'pl' ? 'serii' : 'sets'})`}
                  />
                );
              })}
            </div>
          </div>
        )}
      </header>

      {/* MOBILE TAB TOGGLE (<1024px) */}
      <div className="lg:hidden flex border-b border-slate-800 bg-slate-950 p-2 shrink-0">
        <button
          type="button"
          onClick={() => setMobileTab("timeline")}
          className={`flex-1 py-2 rounded-xl text-xs font-bold transition cursor-pointer flex items-center justify-center gap-2 ${
            mobileTab === 'timeline' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400'
          }`}
        >
          <span className="material-symbols-outlined text-sm">format_list_numbered</span>
          <span>{lang === 'pl' ? `Trening (${exercises.length})` : `Timeline (${exercises.length})`}</span>
        </button>
        <button
          type="button"
          onClick={() => setMobileTab("catalog")}
          className={`flex-1 py-2 rounded-xl text-xs font-bold transition cursor-pointer flex items-center justify-center gap-2 ${
            mobileTab === 'catalog' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400'
          }`}
        >
          <span className="material-symbols-outlined text-sm">search</span>
          <span>{lang === 'pl' ? `Katalog (${availableCatalog.length})` : `Catalog (${availableCatalog.length})`}</span>
        </button>
      </div>

      {/* 2 & 3. MAIN SPLIT-SCREEN WORKOUT BUILDER */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0 overflow-hidden">
        
        {/* LEFT PANEL (58% Width) — WORKOUT TIMELINE (FIXED SCROLL CONTAINER) */}
        <main className={`lg:w-[58%] flex-1 p-4 lg:p-6 overflow-y-auto space-y-4 border-r border-slate-800/80 ${
          mobileTab === 'catalog' ? 'hidden lg:block' : 'block'
        }`}>
          {exercises.length === 0 ? (
            <div className="h-full min-h-[340px] flex flex-col items-center justify-center text-center p-8 bg-slate-950/40 border-2 border-dashed border-slate-800 rounded-2xl space-y-3">
              <div className="w-14 h-14 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
                <span className="material-symbols-outlined text-2xl">add_task</span>
              </div>
              <h3 className="text-base font-extrabold text-slate-200 m-0">
                {lang === 'pl' ? "Brak ćwiczeń w szablonie" : "Routine timeline is empty"}
              </h3>
              <p className="text-xs text-slate-400 max-w-sm m-0 leading-relaxed">
                {lang === 'pl' 
                  ? "Dodaj ćwiczenia z prawego katalogu klikając przycisk + Dodaj, aby zbudować i skonfigurować swój plan treningowy."
                  : "Add exercises from the right catalog by clicking + Add to configure sets, reps, and cues."}
              </p>
            </div>
          ) : (
            <div className="space-y-4 pb-8">
              {exercises.map((ex, exIdx) => {
                const isSuperset = !!ex.supersetGroup;

                return (
                  <div
                    key={ex.uid || exIdx}
                    className={`bg-slate-900/90 border rounded-2xl p-4 lg:p-5 space-y-4 shadow-xl transition-all relative ${
                      isSuperset 
                        ? 'border-l-4 border-l-indigo-500 border-indigo-500/40 bg-gradient-to-r from-indigo-950/40 via-slate-900 to-slate-900 shadow-indigo-950/40' 
                        : 'border-slate-800 hover:border-slate-700'
                    }`}
                  >
                    {/* EXERCISE CARD HEADER */}
                    <div className="flex items-center justify-between gap-3 flex-wrap border-b border-slate-800/80 pb-3">
                      <div className="flex items-center gap-2.5 flex-wrap min-w-0 flex-1">
                        
                        {/* Drag Handle */}
                        <div className="flex items-center gap-1.5 text-slate-500 cursor-grab active:cursor-grabbing">
                          <span className="material-symbols-outlined text-lg text-slate-400" title={lang === 'pl' ? "Przeciągnij, aby zmienić kolejność" : "Drag to reorder"}>drag_indicator</span>
                          <span className={`w-7 h-7 rounded-lg font-bold flex items-center justify-center text-xs shrink-0 border ${
                            isSuperset 
                              ? 'bg-indigo-600 text-white border-indigo-400 shadow-sm' 
                              : 'bg-slate-800 text-slate-200 border-slate-700'
                          }`}>
                            {isSuperset ? `${ex.supersetGroup}${exIdx + 1}` : exIdx + 1}
                          </span>
                        </div>

                        {/* Title (Flex Naturally Without Premature Line-Break) & Badges */}
                        <div className="min-w-0 flex-1">
                          <h4 className="font-extrabold text-sm lg:text-base text-white m-0 tracking-tight leading-snug whitespace-nowrap overflow-hidden text-ellipsis">
                            {toTitleCase(lang === 'pl' ? (ex.namePL || ex.name) : (ex.nameEN || ex.name))}
                          </h4>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs uppercase tracking-wider text-slate-400 font-medium">
                              {translateTerm(ex.bodyPart || ex.category, lang)} • {translateTerm(ex.equipment, lang)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Header Actions: Default Rest, Superset Toggle, Delete Card (No Redundant Arrows) */}
                      <div className="flex items-center gap-2 shrink-0 flex-wrap">
                        


                        {/* Superset Toggle Button */}
                        <button
                          type="button"
                          onClick={() => handleToggleSuperset(exIdx)}
                          className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer border flex items-center gap-1 ${
                            isSuperset 
                              ? 'bg-indigo-600 text-white border-indigo-400 shadow-sm' 
                              : 'bg-slate-950 text-slate-400 hover:text-white border-slate-800'
                          }`}
                          title={lang === 'pl' ? "Połącz w obwód / superserię" : "Link into superset"}
                        >
                          <span className="material-symbols-outlined text-xs">link</span>
                          <span>{isSuperset ? `Super (${ex.supersetGroup})` : (lang === 'pl' ? '+ Superseria' : '+ Superset')}</span>
                        </button>

                        {/* Delete Card Button */}
                        <button
                          type="button"
                          onClick={() => setExercises(prev => prev.filter((_, idx) => idx !== exIdx))}
                          className="bg-slate-950 hover:bg-rose-950/60 text-slate-400 hover:text-rose-400 p-1.5 rounded-lg transition border border-slate-800 cursor-pointer"
                          title={lang === 'pl' ? "Usuń ćwiczenie" : "Delete exercise"}
                        >
                          <span className="material-symbols-outlined text-xs">delete</span>
                        </button>
                      </div>
                    </div>

                    {/* CONFIGURABLE SET TABLE */}
                    <div className="bg-slate-950/80 border border-slate-800/80 rounded-xl p-3 space-y-2 shadow-inner">
                      <div className="grid grid-cols-12 gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-400 px-2 pb-1.5 border-b border-slate-800">
                        <div className="col-span-1 text-center">#</div>
                        <div className="col-span-3 text-left">{lang === 'pl' ? 'TYP SERII' : 'SET TYPE'}</div>
                        <div className="col-span-3 text-center">{lang === 'pl' ? 'POWTÓRZENIA' : 'REPS'}</div>
                        <div className="col-span-3 text-center">{lang === 'pl' ? 'CIĘŻAR' : 'LOAD'}</div>
                        <div className="col-span-1 text-center">RPE</div>
                        <div className="col-span-1 text-center"></div>
                      </div>

                      {(ex.setsList || []).map((sRow, sIdx) => (
                        <div key={sIdx} className="grid grid-cols-12 gap-2 items-center text-xs group/row hover:bg-slate-900/60 p-1 rounded-lg transition">
                          {/* Set Number */}
                          <div className="col-span-1 text-center font-bold text-slate-400">
                            {sRow.setNum}
                          </div>

                          {/* Set Type Dropdown (Localized) */}
                          <div className="col-span-3">
                            <select
                              value={sRow.type || 'Normal'}
                              onChange={(e) => handleUpdateSetField(exIdx, sIdx, 'type', e.target.value)}
                              className="w-full bg-slate-900 border border-slate-800 text-slate-200 font-bold text-[11px] rounded-lg px-2 py-1 outline-none focus:border-indigo-500 cursor-pointer"
                            >
                              <option value="Warm-up">{lang === 'pl' ? 'Rozgrzewka' : 'Warm-up'}</option>
                              <option value="Normal">{lang === 'pl' ? 'Robocza' : 'Working Set'}</option>
                              <option value="Drop-set">{lang === 'pl' ? 'Drop-set' : 'Drop Set'}</option>
                              <option value="Failure">{lang === 'pl' ? 'Do odmowy' : 'Failure Set'}</option>
                            </select>
                          </div>

                          {/* Reps Input */}
                          <div className="col-span-3">
                            <input
                              type="text"
                              value={sRow.reps || "10"}
                              onChange={(e) => handleUpdateSetField(exIdx, sIdx, 'reps', e.target.value)}
                              placeholder="8–10"
                              className="w-full bg-slate-900 border border-slate-800 text-white font-bold text-center text-xs rounded-lg px-1.5 py-1 outline-none focus:border-indigo-500"
                            />
                          </div>

                          {/* Weight / Load Input with Unit Control */}
                          <div className="col-span-3 flex items-center gap-1">
                            <input
                              type="text"
                              value={sRow.weight || ""}
                              onChange={(e) => handleUpdateSetField(exIdx, sIdx, 'weight', e.target.value)}
                              placeholder="70 / 75%"
                              className="w-full bg-slate-900 border border-slate-800 text-emerald-400 font-bold text-center text-xs rounded-lg px-1.5 py-1 outline-none focus:border-emerald-500"
                            />
                            <select
                              value={sRow.weightUnit || 'kg'}
                              onChange={(e) => handleUpdateSetField(exIdx, sIdx, 'weightUnit', e.target.value)}
                              className="bg-slate-900 border border-slate-800 text-emerald-300 font-bold text-[10px] rounded-lg px-1 py-1 outline-none cursor-pointer shrink-0"
                            >
                              <option value="kg">kg</option>
                              <option value="lbs">lbs</option>
                              <option value="%">%</option>
                            </select>
                          </div>

                          {/* Target RPE */}
                          <div className="col-span-1">
                            <input
                              type="text"
                              value={sRow.rpe || "8"}
                              onChange={(e) => handleUpdateSetField(exIdx, sIdx, 'rpe', e.target.value)}
                              placeholder="8"
                              className="w-full bg-slate-900 border border-slate-800 text-amber-300 font-bold text-center text-xs rounded-lg px-1 py-1 outline-none focus:border-indigo-500"
                            />
                          </div>



                          {/* Delete Set Row Button (Softened on hover) */}
                          <div className="col-span-1 flex justify-center items-center">
                            <button
                              type="button"
                              disabled={(ex.setsList || []).length <= 1}
                              onClick={() => handleRemoveSetRow(exIdx, sIdx)}
                              className="w-7 h-7 rounded-lg bg-slate-900/60 text-slate-500 hover:text-rose-400 opacity-40 hover:opacity-100 group-hover/row:opacity-80 border border-slate-800/80 transition cursor-pointer flex items-center justify-center shadow-inner"
                              title={lang === 'pl' ? "Usuń tę serię" : "Delete set"}
                            >
                              <span className="material-symbols-outlined text-xs">close</span>
                            </button>
                          </div>
                        </div>
                      ))}

                      {/* Add Set Button (Inherits previous set reps/load/rest) */}
                      <div className="pt-1 flex justify-start">
                        <button
                          type="button"
                          onClick={() => handleAddSetRow(exIdx)}
                          className="text-xs font-bold text-indigo-400 hover:text-white bg-indigo-500/10 hover:bg-indigo-600 px-3 py-1 rounded-lg transition cursor-pointer flex items-center gap-1 border border-indigo-500/20"
                        >
                          <span className="material-symbols-outlined text-xs">add</span>
                          <span>{lang === 'pl' ? 'Dodaj Serię' : 'Add Set'}</span>
                        </button>
                      </div>
                    </div>

                    {/* EXERCISE NOTES / CUES (TOGGLEABLE TEXTAREA) */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-xs uppercase tracking-wider text-slate-400 font-bold">
                        <span className="flex items-center gap-1">
                          <span className="material-symbols-outlined text-xs text-indigo-400">edit_note</span>
                          <span>{lang === 'pl' ? 'Wskazówki i tempo' : 'Coaching cues & tempo'}</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            const updated = [...exercises];
                            updated[exIdx].notesCollapsed = !updated[exIdx].notesCollapsed;
                            setExercises(updated);
                          }}
                          className="text-[10px] font-bold text-slate-400 hover:text-white cursor-pointer bg-slate-950 border border-slate-800 px-2 py-0.5 rounded-md"
                        >
                          {ex.notesCollapsed ? (lang === 'pl' ? '+ Pokaż' : '+ Expand') : (lang === 'pl' ? '– Zwiń' : '– Collapse')}
                        </button>
                      </div>

                      {!ex.notesCollapsed && (
                        <textarea
                          rows={2}
                          value={ex.comment ?? ex.notes ?? ex.hints ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            const updated = [...exercises];
                            updated[exIdx] = { ...updated[exIdx], comment: val, notes: val, hints: val };
                            setExercises(updated);
                          }}
                          placeholder={lang === 'pl' ? "np. Tempo 3-0-1-0, pauza na dole, mocne spięcie..." : "e.g. 3-0-1-0 tempo, pause at bottom..."}
                          className="w-full bg-slate-950 border border-slate-800 focus:border-indigo-500 text-slate-200 font-medium text-xs rounded-xl p-2.5 outline-none resize-y shadow-inner leading-relaxed"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>

        {/* RIGHT PANEL (42% Width) — STICKY EXERCISE CATALOG (FIXED SCROLL CONTAINER) */}
        <aside className={`lg:w-[42%] flex-1 bg-slate-950 p-4 lg:p-6 overflow-y-auto space-y-4 ${
          mobileTab === 'timeline' ? 'hidden lg:block' : 'block'
        }`}>
          
          {/* Header & Tabs */}
          <div className="space-y-3 sticky top-0 bg-slate-950 z-10 pb-2 border-b border-slate-800/60">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1.5">
                <span className="material-symbols-outlined text-base text-indigo-400">search</span>
                <span>{lang === 'pl' ? "Katalog ćwiczeń" : "Exercise library"}</span>
              </span>

              {/* Global / Custom Toggle Buttons (Localized) */}
              {!isGlobalOnly && customExercises.length > 0 && (
                <div className="flex items-center gap-1.5 bg-slate-900 p-1 rounded-xl border border-slate-800">
                  <button
                    type="button"
                    onClick={() => setCatalogTab('global')}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition cursor-pointer ${
                      catalogTab === 'global' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    {lang === 'pl' ? 'Globalne' : 'Global'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCatalogTab('custom')}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition cursor-pointer ${
                      catalogTab === 'custom' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    {lang === 'pl' ? 'Autorskie' : 'Custom'}
                  </button>
                </div>
              )}
            </div>

            {/* Instant Search Bar */}
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">
                search
              </span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={lang === 'pl' ? "Szukaj po nazwie lub partii..." : "Search by name or muscle..."}
                className="w-full bg-slate-900 border border-slate-800 focus:border-indigo-500 text-white font-bold text-xs rounded-xl pl-9 pr-9 py-2.5 outline-none shadow-inner transition"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white cursor-pointer"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Combobox Dropdown Filters (Muscle & Equipment) + Instant Clear Filters Button */}
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                
                {/* Muscle Group Combobox */}
                <div className="space-y-1">
                  <label className="block text-xs uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1">
                    <span className="material-symbols-outlined text-xs text-indigo-400">fitness_center</span>
                    <span>{lang === 'pl' ? 'Partia ciała:' : 'Muscle group:'}</span>
                  </label>
                  <select
                    value={selectedMuscle}
                    onChange={(e) => setSelectedMuscle(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-800 focus:border-indigo-500 text-slate-200 font-bold text-xs rounded-xl px-2.5 py-2 outline-none cursor-pointer shadow-inner"
                  >
                    <option value="ALL">{lang === 'pl' ? "Wszystkie partie ciała" : "All body parts"}</option>
                    <option value="chest">{lang === 'pl' ? "Klatka piersiowa (Chest)" : "Chest"}</option>
                    <option value="back">{lang === 'pl' ? "Plecy i grzbiet (Back)" : "Back"}</option>
                    <option value="shoulders">{lang === 'pl' ? "Barki (Shoulders)" : "Shoulders"}</option>
                    <option value="upper arms">{lang === 'pl' ? "Ramiona (Upper arms)" : "Upper arms"}</option>
                    <option value="lower arms">{lang === 'pl' ? "Przedramiona (Forearms)" : "Forearms"}</option>
                    <option value="upper legs">{lang === 'pl' ? "Uda i pośladki (Legs)" : "Upper legs"}</option>
                    <option value="lower legs">{lang === 'pl' ? "Łydki (Calves)" : "Lower legs"}</option>
                    <option value="waist">{lang === 'pl' ? "Brzuch / Core" : "Waist / Core"}</option>
                    <option value="cardio">{lang === 'pl' ? "Kardio" : "Cardio"}</option>
                    <option value="neck">{lang === 'pl' ? "Szyja" : "Neck"}</option>
                  </select>
                </div>

                {/* Equipment Combobox */}
                <div className="space-y-1">
                  <label className="block text-xs uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1">
                    <span className="material-symbols-outlined text-xs text-slate-400">build</span>
                    <span>{lang === 'pl' ? 'Sprzęt:' : 'Equipment:'}</span>
                  </label>
                  <select
                    value={selectedEquipment}
                    onChange={(e) => setSelectedEquipment(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-800 focus:border-indigo-500 text-slate-200 font-bold text-xs rounded-xl px-2.5 py-2 outline-none cursor-pointer shadow-inner"
                  >
                    <option value="ALL">{lang === 'pl' ? "Wszystek sprzęt" : "All equipment"}</option>
                    <option value="barbell">{lang === 'pl' ? "Sztanga" : "Barbell"}</option>
                    <option value="dumbbell">{lang === 'pl' ? "Hantle" : "Dumbbells"}</option>
                    <option value="cable">{lang === 'pl' ? "Wyciąg" : "Cable"}</option>
                    <option value="leverage machine">{lang === 'pl' ? "Maszyny" : "Machines"}</option>
                    <option value="body weight">{lang === 'pl' ? "Ciężar ciała" : "Body weight"}</option>
                    <option value="band">{lang === 'pl' ? "Guma oporowa" : "Bands"}</option>
                    <option value="kettlebell">Kettlebell</option>
                    <option value="smith machine">Suwnica Smitha</option>
                  </select>
                </div>
              </div>

              {/* Clear Filters Trigger (Visible when filters active) */}
              {hasActiveFilters && (
                <div className="flex justify-end pt-1">
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="text-xs font-bold text-indigo-400 hover:text-white bg-indigo-500/10 hover:bg-indigo-600 px-3 py-1 rounded-lg transition cursor-pointer flex items-center gap-1 border border-indigo-500/20"
                  >
                    <span className="material-symbols-outlined text-xs">filter_alt_off</span>
                    <span>{lang === 'pl' ? 'Wyczyść filtry' : 'Reset filters'}</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Exercise List / Cards Grid */}
          <div className="space-y-2.5 pr-1 pb-8">
            {availableCatalog.length === 0 ? (
              <div className="p-8 text-center text-slate-500 font-semibold text-xs bg-slate-900/50 rounded-2xl border border-slate-800">
                {lang === 'pl' ? "Brak ćwiczeń spełniających wybrane kryteria" : "No exercises match your search query"}
              </div>
            ) : (
              availableCatalog.map(ex => {
                const addedCount = exercises.filter(item => item.id === ex.id || item.name === ex.name).length;
                const isAdded = addedCount > 0;
                const thumbUrl = ex.imageUrl || ex.gif_url || ex.image || "";

                return (
                  <div
                    key={ex.id}
                    className={`p-3 rounded-2xl border transition flex items-center justify-between gap-3 shadow-md ${
                      isAdded 
                        ? 'bg-indigo-950/40 border-indigo-500/40 text-indigo-200' 
                        : 'bg-slate-900 hover:bg-slate-850 border-slate-800 hover:border-slate-700 text-slate-200'
                    }`}
                  >
                    {/* Thumbnail framed inside dark container & Title */}
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      {thumbUrl ? (
                        <div 
                          onClick={() => setPreviewMedia({ url: thumbUrl, title: toTitleCase(ex.namePL || ex.name) })}
                          className="w-12 h-12 rounded-xl overflow-hidden bg-slate-900 border border-slate-700/60 p-1 shrink-0 cursor-pointer hover:opacity-80 relative group shadow-inner"
                        >
                          <img src={thumbUrl} alt="" className="w-full h-full object-contain" />
                          <span className="material-symbols-outlined absolute inset-0 m-auto text-xs text-white opacity-0 group-hover:opacity-100 flex items-center justify-center bg-black/60">visibility</span>
                        </div>
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-slate-900 border border-slate-700/60 p-1 flex items-center justify-center text-slate-400 shrink-0 text-sm font-bold shadow-inner">
                          <span className="material-symbols-outlined text-base">fitness_center</span>
                        </div>
                      )}

                      <div className="min-w-0 flex-1">
                        <strong className="text-xs font-extrabold text-white block leading-snug tracking-tight">
                          {toTitleCase(lang === 'pl' ? (ex.namePL || ex.name) : (ex.nameEN || ex.name))}
                        </strong>
                        <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                          <span className="text-xs uppercase tracking-wider text-slate-400 font-medium">
                            {translateTerm(ex.bodyPart || ex.category, lang)} • {translateTerm(ex.equipment, lang)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Add Button */}
                    <button
                      type="button"
                      onClick={() => handleAddExerciseToTimeline(ex)}
                      className={`px-3 py-2 rounded-xl text-xs font-bold cursor-pointer transition shrink-0 flex items-center gap-1 ${
                        isAdded
                          ? 'bg-indigo-600 text-white hover:bg-indigo-500 shadow-sm'
                          : 'bg-slate-800 hover:bg-indigo-600 text-white'
                      }`}
                    >
                      <span className="material-symbols-outlined text-xs">{isAdded ? 'check' : 'add'}</span>
                      <span>{isAdded ? (lang === 'pl' ? `Dodano (${addedCount})` : `Added (${addedCount})`) : (lang === 'pl' ? 'Dodaj' : 'Add')}</span>
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </aside>
      </div>

      {/* LIGHTBOX MEDIA MODAL PREVIEW */}
      {previewMedia && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPreviewMedia(null)}
        >
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-md w-full space-y-4 relative shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <h3 className="text-sm font-bold text-white m-0 truncate">{previewMedia.title}</h3>
              <button 
                type="button" 
                onClick={() => setPreviewMedia(null)}
                className="text-slate-400 hover:text-white font-bold text-sm"
              >
                ✕
              </button>
            </div>
            <div className="w-full h-64 rounded-xl overflow-hidden bg-black border border-slate-800 flex items-center justify-center">
              <img src={previewMedia.url} alt="" className="w-full h-full object-contain p-2" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
